import React, { useEffect, useRef } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";

/**
 * LanguageShell — global UI translator driven by user.preferred_language.
 * ---------------------------------------------------------------------
 * Sep 7 2026. Admins can assign each user a language (en/zh) from the
 * Users admin. When the current user's preferred_language is "zh", this
 * component walks the DOM after every render / route change and swaps
 * English text nodes + input placeholders + option labels + button titles
 * to Chinese via POST /api/ai/translate_batch.
 *
 * Design notes:
 *   • Skips anything inside [data-notranslate], <code>, <pre>, <script>,
 *     <style>, .font-mono elements and inputs of type text/number/date.
 *   • Skips strings with no ASCII letters (pure numbers, IDs, SKUs, CJK).
 *   • Uses a persistent localStorage cache so the second load is instant.
 *   • Uses a MutationObserver to catch async content, table row loads,
 *     modal drawers etc.
 *   • Batches new strings on a 250 ms debounce (up to 180 per request).
 */

const CACHE_KEY = "erp_ui_translations_v1";
const BATCH_SIZE = 180;
const DEBOUNCE_MS = 250;
const SKIP_TAGS = new Set([
  "SCRIPT", "STYLE", "CODE", "PRE", "NOSCRIPT", "IMG", "SVG", "IFRAME",
]);
// Only translate specific input attributes; skip <input> value which is user data.
const ATTR_TARGETS = ["placeholder", "title", "aria-label"];

function loadCache() {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function saveCache(cache) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(cache));
  } catch {
    /* quota — ignore */
  }
}

function hasAsciiLetter(s) {
  return /[A-Za-z]/.test(s);
}

function shouldSkipElement(el) {
  if (!el) return true;
  if (SKIP_TAGS.has(el.tagName)) return true;
  if (el.closest && el.closest("[data-notranslate]")) return true;
  if (el.closest && el.closest(".font-mono")) return true;
  if (el.tagName === "INPUT" || el.tagName === "TEXTAREA") {
    // We still want to translate their placeholders below, but skip .value
    return false;
  }
  return false;
}

function collectTextNodes(root, out) {
  const walker = document.createTreeWalker(
    root,
    NodeFilter.SHOW_TEXT,
    {
      acceptNode(node) {
        const t = (node.nodeValue || "").trim();
        if (!t || t.length < 2) return NodeFilter.FILTER_REJECT;
        if (!hasAsciiLetter(t)) return NodeFilter.FILTER_REJECT;
        const p = node.parentNode;
        if (!p) return NodeFilter.FILTER_REJECT;
        if (SKIP_TAGS.has(p.tagName)) return NodeFilter.FILTER_REJECT;
        if (p.closest && p.closest("[data-notranslate]")) return NodeFilter.FILTER_REJECT;
        if (p.closest && p.closest(".font-mono")) return NodeFilter.FILTER_REJECT;
        return NodeFilter.FILTER_ACCEPT;
      },
    },
  );
  let n;
  // eslint-disable-next-line no-cond-assign
  while ((n = walker.nextNode())) out.push(n);
}

function collectAttrTargets(root, out) {
  const nodes = root.nodeType === 1 ? [root, ...root.querySelectorAll("*")] : root.querySelectorAll("*");
  for (const el of nodes) {
    if (shouldSkipElement(el)) continue;
    for (const attr of ATTR_TARGETS) {
      if (!el.hasAttribute(attr)) continue;
      const v = el.getAttribute(attr);
      const t = (v || "").trim();
      if (!t || t.length < 2 || !hasAsciiLetter(t)) continue;
      out.push({ el, attr, original: v, trimmed: t });
    }
    // <option> label = its text (already collected as text node) — nothing else.
  }
}

class Translator {
  constructor(target) {
    this.target = target;
    this.cache = loadCache();
    this.pending = new Set();
    this.debounce = null;
    this.applied = new WeakMap(); // node → last-translated-value (to detect React resets)
  }

  translateNode(node) {
    const src = (node.nodeValue || "").trim();
    if (!src) return;
    if (!hasAsciiLetter(src)) return;
    const hit = this.cache[src];
    if (hit) {
      // Preserve leading/trailing whitespace so layout doesn't collapse.
      const raw = node.nodeValue || "";
      const lead = raw.match(/^\s*/)?.[0] || "";
      const trail = raw.match(/\s*$/)?.[0] || "";
      const next = `${lead}${hit}${trail}`;
      if (node.nodeValue !== next) node.nodeValue = next;
      this.applied.set(node, hit);
    } else {
      this.pending.add(src);
      this.scheduleFlush();
    }
  }

  translateAttr({ el, attr, original, trimmed }) {
    const hit = this.cache[trimmed];
    if (hit) {
      const lead = original.match(/^\s*/)?.[0] || "";
      const trail = original.match(/\s*$/)?.[0] || "";
      const next = `${lead}${hit}${trail}`;
      if (el.getAttribute(attr) !== next) el.setAttribute(attr, next);
    } else {
      this.pending.add(trimmed);
      this.scheduleFlush();
    }
  }

  scan(root = document.body) {
    if (!root) return;
    const texts = [];
    const attrs = [];
    collectTextNodes(root, texts);
    collectAttrTargets(root, attrs);
    for (const n of texts) this.translateNode(n);
    for (const a of attrs) this.translateAttr(a);
  }

  scheduleFlush() {
    if (this.debounce) return;
    this.debounce = setTimeout(() => {
      this.debounce = null;
      this.flush();
    }, DEBOUNCE_MS);
  }

  async flush() {
    if (!this.pending.size) return;
    const batch = Array.from(this.pending).slice(0, BATCH_SIZE);
    // Only clear the strings we're about to send.
    for (const t of batch) this.pending.delete(t);
    try {
      const { data } = await api.post("/ai/translate_batch", {
        texts: batch,
        target_lang: this.target,
      });
      const map = data?.translations || {};
      let dirty = false;
      for (const [k, v] of Object.entries(map)) {
        if (v && v !== k) {
          this.cache[k] = v;
          dirty = true;
        } else if (v === k) {
          // still record so we don't re-ask forever
          this.cache[k] = k;
          dirty = true;
        }
      }
      if (dirty) saveCache(this.cache);
      // Re-scan so the newly cached strings apply immediately.
      this.scan(document.body);
    } catch (_e) {
      // On failure keep going — leave the strings in English so ops isn't
      // stranded. They'll retry on the next tick.
    }
    if (this.pending.size) this.scheduleFlush();
  }
}

export default function LanguageShell() {
  const { user } = useAuth();
  const translatorRef = useRef(null);
  const observerRef = useRef(null);

  useEffect(() => {
    const lang = (user && user.preferred_language) || "en";
    // Reflect on <html> so consumers can style with :lang() if they want.
    try { document.documentElement.setAttribute("lang", lang); } catch { /* noop */ }

    // Tear down any previous observer before re-init.
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }

    if (lang !== "zh") {
      // Reload the page ONCE when switching back to en so any translated
      // text nodes are restored from React's virtual DOM (React will
      // re-render on the next update; a hard reload is only needed if the
      // user is currently viewing translated content and switched
      // preference in the same tab — rare enough to leave to a manual
      // refresh via the toast in the Users edit drawer).
      translatorRef.current = null;
      return;
    }

    // Init translator + observer.
    const t = new Translator(lang);
    translatorRef.current = t;
    // Initial scan on next microtask so React finishes commit first.
    setTimeout(() => t.scan(document.body), 0);

    const observer = new MutationObserver((muts) => {
      for (const m of muts) {
        if (m.type === "characterData") {
          if (m.target?.nodeType === 3) t.translateNode(m.target);
        } else if (m.type === "childList") {
          m.addedNodes.forEach((n) => {
            if (n.nodeType === 3) t.translateNode(n);
            else if (n.nodeType === 1) t.scan(n);
          });
        } else if (m.type === "attributes" && m.target?.nodeType === 1) {
          const el = m.target;
          const attr = m.attributeName;
          if (ATTR_TARGETS.includes(attr) && el.hasAttribute(attr)) {
            const v = el.getAttribute(attr);
            const trimmed = (v || "").trim();
            if (trimmed && hasAsciiLetter(trimmed) && !shouldSkipElement(el)) {
              t.translateAttr({ el, attr, original: v, trimmed });
            }
          }
        }
      }
    });
    observer.observe(document.body, {
      subtree: true,
      childList: true,
      characterData: true,
      attributes: true,
      attributeFilter: ATTR_TARGETS,
    });
    observerRef.current = observer;

    return () => {
      observer.disconnect();
      observerRef.current = null;
    };
  }, [user && user.preferred_language]);

  return null;
}
